//@ts-check
/**
 * The things a person wants — the client's whole view of them.
 *
 * Every mutation goes through this module rather than calling the API from a component. Each one
 * is an optimistic local change followed by a request.
 *
 * Optimism is not decoration here. Capture has to feel instant on a phone with one bar, which
 * means the thing appears the moment it is typed and the request catches up afterwards.
 *
 * Capture goes one step further, because a want spotted in a shop with no signal has to be as safe
 * as a photo in the camera roll. It is written to the device's outbox (`$lib/outbox.js`) before
 * it is sent, and stays there until the server has it: a send that cannot get through leaves the
 * thing on screen as "waiting to send", and `flushOutbox` sends it when the app next comes back
 * online, into view or up. The thing's id is minted here, at capture, so a send whose answer was
 * lost can be repeated without saving it twice. Edits and removals are not queued — they need the
 * server's copy, and are made at leisure rather than in the moment.
 */
import ky, { isHTTPError, isNetworkError, isTimeoutError } from "ky";
import { ulid } from "ulid";
import { getfetchopts } from "$lib/utils/utils.js";
import { uploadPhoto } from "$lib/photos.svelte.js";
import { auth } from "$lib/auth.svelte.js";
import { fileOnList, loadLists } from "$lib/lists.svelte.js";
import { allEntries, askToPersist, putEntry, removeEntry } from "$lib/outbox.js";
import { record } from "$lib/captureprobe.js";
import { showError } from "$lib/notice.svelte.js";
import { limitReached } from "$lib/limits.js";
import { getTranslations } from "$lib/utils/locale.svelte.js";
import messages from "$lib/messages/things.js";
import { apiBase } from "$lib/env.js";

// Relative on the web; the app host inside the iOS app ($lib/env.js).
const basePath = apiBase();

/**
 * The collection endpoint keeps its trailing slash.
 *
 * `api/things` is the API's mapping key, so the collection lives at the API's root route — and
 * API Gateway only resolves that with the slash present. Without it the request matches no route
 * and comes back as a bare 404, which looks like a missing endpoint rather than a URL quirk.
 * Single-thing paths need no such thing.
 */
const THINGS_URL = `${basePath}/api/things/`;

/** @type {{ items: any[], loading: boolean, loaded: boolean, error: string, loadedAt: number }} */
export const things = $state({
  items: [],
  loading: false,
  loaded: false,
  error: "",
  loadedAt: 0,
});

/**
 * How stale data may be before coming back to the app refetches it.
 *
 * An installed PWA has no address bar and no reload, so "I added it on my laptop, now show me on
 * my phone" has to happen by itself. Returning to the app after more than this refetches; flicking
 * away and straight back does not.
 */
const STALE_AFTER_MS = 20_000;

/**
 * How long after saving a location that has not arrived yet is still worth attaching. Past this
 * the owner has likely walked on, and a fix would say where they went next, not where they saw it.
 */
const LATE_FIX_MS = 60_000;

/**
 * Load everything the signed-in user wants, newest first.
 *
 * Whatever is already on screen stays there while the request is in flight: a refresh that blanks
 * the list and fills it back in reads as a bug, especially on a slow connection.
 */
export async function loadThings() {
  things.loading = true;
  things.error = "";
  // The lists are drawn from the things, so every refresh of one refreshes both. Not awaited: the
  // things show the moment they arrive and the list chips fill in when theirs does, rather than the
  // whole screen waiting on the slower of the two. Awaited at the end instead, so a caller waiting
  // on the load — the pull, Home's cover — waits for both.
  const listsLoaded = loadLists();
  try {
    const fetchopts = await getfetchopts();
    const res = await ky.get(THINGS_URL, fetchopts).json();
    const server = res.things || [];
    // A capture still saving or waiting to send is not on the server yet, and replacing the list
    // wholesale would make it vanish from the screen while it is still on its way.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- a lookup, never rendered
    const onServer = new Set(server.map((/** @type {any} */ t) => t.thingId));
    const local = things.items.filter((t) => (t.pending || t.queued) && !onServer.has(t.thingId));
    things.items = [...local, ...server].sort(newestFirst);
    things.loaded = true;
    things.loadedAt = Date.now();
    watchDetails();
  } catch (err) {
    console.error("Failed to load things", err);
    things.error = err.message || "Could not load your things";
  } finally {
    things.loading = false;
  }
  await listsLoaded;
}

/**
 * Refetch if the data has gone stale. Safe to call often — on mount, on focus, on becoming
 * visible — because it does nothing when the data is fresh or a request is already running.
 *
 * @param {{ force?: boolean }} [options]
 */
export function refreshThings({ force = false } = {}) {
  if (things.loading) return;
  if (!force && things.loadedAt && Date.now() - things.loadedAt < STALE_AFTER_MS) return;
  return loadThings();
}

/**
 * Capture a thing from one box of text — a pasted link, a few words, or both — and optionally a
 * photo.
 *
 * The card appears at once under the id minted here, and the capture is in the outbox before the
 * first byte is sent. Then it is delivered: `queued` in the result means it could not get through
 * and is waiting on the device, still on screen. Only a capture the server refused outright is
 * removed again and reported, rather than left as a ghost the user believes is saved.
 *
 * A location, when the owner opted in, never holds the save up. Whatever fix is in hand once the
 * photo is up goes in with the thing; if none has arrived, the thing saves without one and the fix
 * is attached when it lands, within `LATE_FIX_MS`. `lateFix` settles with that fix, or null.
 *
 * @param {string} text
 * @param {{ blob: Blob, previewUrl: string } | null} [photo] from `preparePhoto`
 * @param {import("$lib/location.svelte.js").Locator | null} [locator] from `startLocating`
 * @param {{ getDetails?: boolean, listIds?: string[] }} [options] `getDetails`: the _Get details from
 *   the page_ switch, on unless false. `listIds`: the lists to file it on — the one being looked at
 *   when capture started, if any; capture itself never asks
 * @returns {Promise<{ thing: any, queued: boolean, photoFailed: boolean, uploadMs: number, lateFix: Promise<any> | null }>}
 */
export async function captureThing(text, photo = null, locator = null, { getDetails = true, listIds = [] } = {}) {
  const trimmed = (text || "").trim();
  if (!trimmed && !photo) throw new Error("Nothing to save");

  /** @type {import("$lib/outbox.js").OutboxEntry} */
  const entry = {
    thingId: ulid(),
    owner: auth.user?.email || "",
    text: trimmed,
    photo: photo?.blob ?? null,
    imageKey: "",
    location: null,
    queuedAt: Date.now(),
    attempts: 0,
    details: getDetails,
    listIds,
  };
  const card = {
    ...cardFor(entry),
    // Shown until the CDN copy exists, and kept afterwards so the card does not flicker when the
    // saved record replaces this one.
    localImage: photo?.previewUrl || "",
    pending: true,
  };
  // Filed before the card appears, so a thing captured while looking at a list shows on it at once.
  // Only on screen: the lists travel with the capture, through the outbox if need be.
  for (const listId of listIds) fileOnList(listId, entry.thingId, { send: false });
  things.items = [card, ...things.items];
  await stash(entry);

  const result = await deliver(entry, locator);

  if (result.status === "dropped") {
    locator?.stop();
    things.error = result.error?.message || "Could not save that";
    throw result.error;
  }

  const queued = result.status === "queued";
  if (queued) askToPersist();
  const thing = queued ? { ...card, location: entry.location } : result.thing;
  let lateFix = null;
  if (entry.location) locator?.stop();
  else if (locator) lateFix = attachLateFix(entry.thingId, locator);

  return { thing, queued, photoFailed: result.photoFailed, uploadMs: result.uploadMs, lateFix };
}

/**
 * Captures waiting to be sent, by thing id. The outbox in IndexedDB is the durable copy; this is
 * the working one, and it still works when IndexedDB does not (a private window, storage blocked)
 * — the capture then waits only for as long as the app stays open.
 *
 * Bookkeeping, not state: the cards on screen carry what the user sees, so neither this nor
 * `sending` is reactive.
 *
 * @type {Map<string, import("$lib/outbox.js").OutboxEntry>}
 */
// eslint-disable-next-line svelte/prefer-svelte-reactivity
const waiting = new Map();

/** Captures with a send in flight, so a flush never races the capture that is sending them. */
// eslint-disable-next-line svelte/prefer-svelte-reactivity
const sending = new Set();

/** @param {import("$lib/outbox.js").OutboxEntry} entry */
async function stash(entry) {
  waiting.set(entry.thingId, entry);
  try {
    await putEntry(entry);
  } catch (err) {
    console.warn("Could not write the outbox; this capture waits in memory only", err);
  }
}

/** @param {string} thingId */
async function unstash(thingId) {
  waiting.delete(thingId);
  try {
    await removeEntry(thingId);
  } catch (err) {
    console.warn("Could not clear an outbox entry", err);
  }
}

/**
 * Try to get one capture to the server: its photo first, then the thing.
 *
 * - `sent` — the server has it; it is out of the outbox and its card is the saved record.
 * - `queued` — no way through (no signal, a timeout, a server error, a lapsed session). It stays
 *   in the outbox and its card says it is waiting.
 * - `dropped` — the server refused it and always will. It is out of the outbox and off the screen.
 *
 * A photo the server refuses outright does not sink words that came with it: the thing is saved
 * without it and `photoFailed` says so, which is what capture did before there was an outbox.
 *
 * @param {import("$lib/outbox.js").OutboxEntry} entry
 * @param {import("$lib/location.svelte.js").Locator | null} [locator] only on the first attempt
 * @returns {Promise<{ status: "sent" | "queued" | "dropped" | "busy", thing?: any, error?: any, photoFailed: boolean, uploadMs: number }>}
 */
async function deliver(entry, locator = null) {
  if (sending.has(entry.thingId)) return { status: "busy", photoFailed: false, uploadMs: 0 };
  sending.add(entry.thingId);
  entry.attempts += 1;
  markCard(entry.thingId, { pending: true, queued: false });

  let photoFailed = false;
  let uploadMs = 0;
  try {
    // Straight to the queue when the phone already knows there is no network, rather than
    // waiting out a timeout. `onLine` can claim a connection that is not there; never the reverse.
    if (!navigator.onLine) throw Object.assign(new Error("Offline"), { offline: true });

    if (entry.photo && !entry.imageKey) {
      const started = performance.now();
      try {
        entry.imageKey = await uploadPhoto(entry.photo);
        entry.photo = null;
        // Recorded at once, so a retry after this point never uploads the photo a second time.
        await stash(entry);
      } catch (err) {
        if (isRetryable(err) || !entry.text) throw err;
        console.error("Photo refused; saving the words without it", err);
        photoFailed = true;
        entry.photo = null;
      } finally {
        uploadMs = Math.round(performance.now() - started);
      }
    }

    const fix = locator?.state.fix;
    if (!entry.location && fix) entry.location = fix;

    const json = {
      thingId: entry.thingId,
      text: entry.text,
      imageKey: entry.imageKey,
      ...(entry.location ? { location: entry.location } : {}),
      ...(entry.details === false ? { details: false } : {}),
      ...(entry.listIds?.length ? { listIds: entry.listIds } : {}),
    };
    const fetchopts = await getfetchopts({ json });
    const res = await ky.post(THINGS_URL, fetchopts).json();
    await unstash(entry.thingId);

    const saved = { ...res.thing, localImage: res.thing.imageKey ? currentLocal(entry.thingId) : "" };
    things.items = things.items.some((t) => t.thingId === entry.thingId)
      ? things.items.map((t) => (t.thingId === entry.thingId ? saved : t))
      : [saved, ...things.items].sort(newestFirst);
    watchDetails();
    return { status: "sent", thing: saved, photoFailed, uploadMs };
  } catch (err) {
    if (isRetryable(err)) {
      console.warn("Capture could not be sent; it is waiting on the device", err);
      await stash(entry);
      markCard(entry.thingId, { pending: false, queued: true, location: entry.location });
      return { status: "queued", error: err, photoFailed, uploadMs };
    }
    console.error("Capture refused", err);
    await unstash(entry.thingId);
    things.items = things.items.filter((t) => t.thingId !== entry.thingId);
    return { status: "dropped", error: err, photoFailed, uploadMs };
  } finally {
    sending.delete(entry.thingId);
  }
}

/**
 * Whether trying again later could work. A refusal (a 4xx other than a timeout, throttling or a
 * lapsed session) cannot, and is dropped; anything else — including an error nobody anticipated —
 * keeps the capture, because a capture silently lost is the one failure this exists to prevent.
 *
 * @param {any} err
 */
function isRetryable(err) {
  if (err?.offline || !navigator.onLine) return true;
  if (isNetworkError(err) || isTimeoutError(err)) return true;
  // Signed out: the capture goes once they are signed back in.
  if (err?.isautherr || err?.isauthzerr) return true;
  if (isHTTPError(err)) {
    const status = err.response.status;
    return status >= 500 || status === 408 || status === 429;
  }
  return true;
}

/**
 * Put captures left on the device back on screen, as waiting. Called once the session is known, so
 * only this person's captures come back — another account's stay in the outbox until they sign in.
 */
export async function restoreOutbox() {
  let entries;
  try {
    entries = await allEntries();
  } catch (err) {
    console.warn("Could not read the outbox", err);
    return;
  }
  const owner = auth.user?.email;
  for (const entry of entries) {
    if (!owner || entry.owner !== owner || waiting.has(entry.thingId)) continue;
    waiting.set(entry.thingId, entry);
    for (const listId of entry.listIds || []) fileOnList(listId, entry.thingId, { send: false });
    if (things.items.some((t) => t.thingId === entry.thingId)) continue;
    const card = {
      ...cardFor(entry),
      localImage: entry.photo ? URL.createObjectURL(entry.photo) : "",
      queued: true,
    };
    things.items = [card, ...things.items].sort(newestFirst);
  }
}

/** @type {Promise<void> | null} */
let flushing = null;

/**
 * Send whatever is waiting, oldest first. Safe to call often — on start, on coming back online, on
 * returning to the app — because it does nothing when the outbox is empty, and joins a flush that
 * is already running. The first capture that still cannot get through ends the run: the rest
 * would only fail the same way.
 */
export function flushOutbox() {
  // Cleared by the promise, not inside the run: a run with nothing to do finishes without ever
  // awaiting, so clearing in its own `finally` would happen before this assignment — leaving a
  // settled promise here that silently swallows every later call.
  flushing ??= runFlush().finally(() => (flushing = null));
  return flushing;
}

async function runFlush() {
  const t = getTranslations(messages);
  const due = [...waiting.values()].sort((a, b) => (a.thingId < b.thingId ? -1 : 1));
  for (const entry of due) {
    if (entry.owner !== auth.user?.email) continue;
    const result = await deliver(entry);
    if (result.status === "queued") break;
    if (result.status === "busy") continue;
    record({
      kind: result.status === "sent" ? "outbox-sent" : "outbox-dropped",
      queuedForMs: Date.now() - entry.queuedAt,
      attempts: entry.attempts,
      hadPhoto: Boolean(entry.imageKey),
      photoFailed: result.photoFailed,
    });
    if (result.status === "dropped") {
      const hit = limitReached(result.error);
      showError(hit ? t("limit_things", { max: hit.max }) : t("outbox_dropped"));
    } else if (result.photoFailed) showError(t("photo_failed"));
  }
}

/**
 * Where getting a thing's details from its page stands: `none` (no link), `off`, `pending`,
 * `filled` or `nothing` (guides/DYNAMODB.md). A record from before the state existed is read the
 * way its old shape meant.
 *
 * @param {any} thing
 * @returns {"none" | "off" | "pending" | "filled" | "nothing"}
 */
export function detailsState(thing) {
  if (!thing?.url) return "none";
  const enriched = thing.enriched;
  return enriched?.state ?? (enriched ? (enriched.via ? "filled" : "nothing") : "none");
}

/** How often a card waiting for its details asks for them, and for how long. */
const POLL_EVERY_MS = 2_500;
const POLL_FOR_MS = 30_000;
/** How long a card that just got its details stays marked as just arrived — the sparkle. */
const ARRIVED_MS = 1_600;

/**
 * Things whose page details are `pending`, and when each started being watched. Bookkeeping, not
 * state: the cards carry what the owner sees.
 *
 * @type {Map<string, number>}
 */
// eslint-disable-next-line svelte/prefer-svelte-reactivity
const watching = new Map();

/** @type {ReturnType<typeof setTimeout> | null} */
let pollTimer = null;

/** @param {any} t */
const awaitingDetails = (t) => t.enriched?.state === "pending" && !t.pending && !t.queued;

/**
 * Make details appear by themselves (wip-archive/page-details/PRODUCT_DETAILS_READERS.md, "Details appear by
 * themselves"). While a card on screen is waiting for its page details, ask the server for that
 * one thing every few seconds, for half a minute; the server usually has them within two. Safe to
 * call often: it only starts watching what is newly `pending`, and keeps one timer.
 *
 * Past half a minute a card is left saying _Getting details…_ until the list next refreshes —
 * returning to the app does that — rather than asking forever. Nothing is asked while the app is
 * out of sight or offline.
 */
export function watchDetails() {
  const now = Date.now();
  for (const t of things.items) if (awaitingDetails(t) && !watching.has(t.thingId)) watching.set(t.thingId, now);
  if (!pollTimer && watching.size) pollTimer = setTimeout(pollDetails, POLL_EVERY_MS);
}

async function pollDetails() {
  pollTimer = null;
  const now = Date.now();
  for (const [thingId, since] of watching) {
    const t = things.items.find((i) => i.thingId === thingId);
    if (!t || !awaitingDetails(t) || now - since > POLL_FOR_MS) watching.delete(thingId);
  }
  if (!watching.size) return;
  if (document.visibilityState === "visible" && navigator.onLine) {
    await Promise.all([...watching.keys()].map(checkDetails));
  }
  if (watching.size && !pollTimer) pollTimer = setTimeout(pollDetails, POLL_EVERY_MS);
}

/** @param {string} thingId */
async function checkDetails(thingId) {
  let fresh;
  try {
    const fetchopts = await getfetchopts({ noconsole: true });
    fresh = (await ky.get(`${basePath}/api/things/${thingId}`, fetchopts).json()).thing;
  } catch (err) {
    // Gone (removed on another device) stops the watch; anything else is tried again next time.
    if (isHTTPError(err) && err.response.status === 404) watching.delete(thingId);
    return;
  }
  if (fresh.enriched?.state === "pending") return;
  watching.delete(thingId);
  // Filled: marked as just arrived for a moment, which is what the card's sparkle keys off.
  const arrived = fresh.enriched?.state === "filled";
  things.items = things.items.map((t) =>
    t.thingId === thingId ? { ...fresh, localImage: t.localImage, justArrived: arrived } : t
  );
  if (arrived) setTimeout(() => markCard(thingId, { justArrived: false }), ARRIVED_MS);
}

/**
 * Ask for a thing's details from its page again — the edit sheet's button, for a thing that is
 * `off` or came back with `nothing`. The card goes back to _Getting details…_ and is watched.
 *
 * @param {string} thingId
 */
export function requestDetails(thingId) {
  return updateThing(thingId, { details: true });
}

/**
 * What a capture looks like on screen before the server has answered — the same shape as a saved
 * thing, so replacing it does not make the card jump.
 *
 * @param {import("$lib/outbox.js").OutboxEntry} entry
 */
function cardFor(entry) {
  const split = splitUrlAndTitle(entry.text);
  return {
    thingId: entry.thingId,
    ...split,
    note: "",
    imageKey: entry.imageKey,
    location: entry.location,
    state: "wanted",
    // As the server will record it: a link's details are `pending` from the moment it is saved.
    enriched: split.url ? { state: entry.details === false ? "off" : "pending" } : null,
  };
}

/**
 * @param {string} thingId
 * @param {Record<string, any>} changes
 */
function markCard(thingId, changes) {
  things.items = things.items.map((t) => (t.thingId === thingId ? { ...t, ...changes } : t));
}

/** Newest first, by id: ids are ULIDs, so this is the order the server lists them in. */
function newestFirst(/** @type {any} */ a, /** @type {any} */ b) {
  return a.thingId < b.thingId ? 1 : a.thingId > b.thingId ? -1 : 0;
}

/**
 * Attach a location that arrived after its thing was saved. Best effort: the thing is already
 * safe, so a fix that never comes, or a request that fails, costs only the location.
 *
 * A capture still waiting on the device takes the fix with it when it goes. One that is mid-send
 * is left alone — patching its entry now could put it back in the outbox after it has landed.
 *
 * @param {string} thingId
 * @param {import("$lib/location.svelte.js").Locator} locator
 */
async function attachLateFix(thingId, locator) {
  const fix = await locator.finish(LATE_FIX_MS);
  if (!fix) return null;

  const entry = waiting.get(thingId);
  if (entry) {
    if (sending.has(thingId) || entry.location) return null;
    entry.location = fix;
    await stash(entry);
    markCard(thingId, { location: fix });
    return fix;
  }

  if (!things.items.some((t) => t.thingId === thingId)) return null;
  try {
    await updateThing(thingId, { location: fix });
    return fix;
  } catch (err) {
    console.error("Failed to attach a late location", err);
    return null;
  }
}

/**
 * Edit a thing. Only the given fields are sent, so a caller that knows about one field cannot
 * blank the rest.
 *
 * A new photo is uploaded first and its key sent with the other changes, so a thing never points
 * at a photo that failed to arrive. Removing the photo is just `imageKey: ""` in `changes`.
 *
 * @param {string} thingId
 * @param {Record<string, any>} changes
 * @param {{ blob: Blob, previewUrl: string } | null} [photo] from `preparePhoto`
 */
export async function updateThing(thingId, changes, photo = null) {
  const before = things.items;
  const local = photo ? { localImage: photo.previewUrl } : "imageKey" in changes ? { localImage: "" } : {};
  things.items = things.items.map((t) => (t.thingId === thingId ? { ...t, ...changes, ...local } : t));

  try {
    const payload = photo ? { ...changes, imageKey: await uploadPhoto(photo.blob) } : changes;
    const fetchopts = await getfetchopts({ json: payload });
    const res = await ky.put(`${basePath}/api/things/${thingId}`, fetchopts).json();
    const saved = { ...res.thing, localImage: photo ? photo.previewUrl : (local.localImage ?? currentLocal(thingId)) };
    things.items = things.items.map((t) => (t.thingId === thingId ? saved : t));
    // A new link, or the owner asking, puts it back to `pending`: watch for the details.
    watchDetails();
    return saved;
  } catch (err) {
    console.error("Failed to update thing", err);
    things.items = before;
    throw err;
  }
}

/** @param {string} thingId */
function currentLocal(thingId) {
  return things.items.find((t) => t.thingId === thingId)?.localImage || "";
}

/**
 * Remove a thing. Capture is cheap on purpose, so undoing a stray one has to be cheap too.
 *
 * @param {string} thingId
 */
export async function deleteThing(thingId) {
  const before = things.items;
  things.items = things.items.filter((t) => t.thingId !== thingId);

  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${basePath}/api/things/${thingId}`, fetchopts);
  } catch (err) {
    console.error("Failed to delete thing", err);
    things.items = before;
    throw err;
  }
}

/**
 * Split typed text into a URL and a title, the same way the API does, so the optimistic row looks
 * like the saved one rather than flickering when it is replaced.
 *
 * @param {string} text
 */
function splitUrlAndTitle(text) {
  const match = text.match(/https?:\/\/\S+/i);
  if (!match) return { title: text, url: "" };
  return { url: match[0], title: text.replace(match[0], "").trim() };
}
